import { CLIENT_ID, useEnvConfig } from "@/env-config";
import { useToast } from "@faro-lotv/flat-ui";
import { AuthProvider } from "@faro-lotv/gate-keepers";
import {
  isCoreApiAuthenticationError,
  useCoreApiClient,
} from "@faro-lotv/service-wires";
import { PropsWithChildren, useCallback } from "react";

/**
 * The HoloBuilder Auth Provider will manage the authentication with the HoloBuilder system
 * using the UserModule iframe.
 *
 * When the app start will inject the UserModule iframe and use that to check if the user it
 * authenticated already or to manage the authentication workflow
 *
 * @returns HoloBuilder authentication provider
 */
export function HBAuthProvider({
  children,
}: PropsWithChildren): JSX.Element | null {
  const configuration = useEnvConfig();
  const coreApiClient = useCoreApiClient(configuration.coreApiUrl, CLIENT_ID);

  const { openToast } = useToast();

  const reportAccessError = useCallback(
    (e: unknown) => {
      const message =
        e instanceof Error || isCoreApiAuthenticationError(e)
          ? e.message
          : JSON.stringify(e);
      openToast({
        title: "Login failed",
        message,
        variant: "error",
        persist: true,
      });
    },
    [openToast],
  );
  const reportFailedLogout = useCallback(
    (e: unknown) => {
      const message =
        e instanceof Error || isCoreApiAuthenticationError(e)
          ? e.message
          : JSON.stringify(e);
      openToast({
        title: "Logout failed",
        message,
        variant: "error",
        persist: true,
      });
    },
    [openToast],
  );

  return (
    <AuthProvider
      ulmUrl={configuration.userModuleUrl}
      coreApiClient={coreApiClient}
      onLogout={() => location.reload()}
      onLoginFailed={reportAccessError}
      onLogoutFailed={reportFailedLogout}
      onShowLoadingScreen={() => {}}
    >
      {children}
    </AuthProvider>
  );
}
